import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { UsersService } from 'src/app/services/users/users.service';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { lastValueFrom } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { UserInfo } from '../../../models/user-info.model';
import { entAnimations } from 'src/app/animations';
import { ProgressSpinnerMode } from '@angular/material/progress-spinner';
import { Error } from 'src/app/models/error.model';
import { CustomValidators } from 'src/app/utils/validators';
import { UserPasswordReset } from 'src/app/models/user-password-reset.model';

@Component({
  selector: 'app-password-reset',
  templateUrl: './password-reset.component.html',
  styleUrls: ['./password-reset.component.scss'],
  encapsulation: ViewEncapsulation.None,
  animations: entAnimations,
  standalone: false,
})
export class PasswordResetComponent implements OnInit {
  currentUser: UserInfo = new UserInfo();

  recoveryToken: string;
  userEmail: string;
  isTokenValid = true;
  resetPasswordForm: FormGroup;

  // API Calls
  isBusy = false;
  isPasswordUpdate = false;
  errorUpdatePassword = false;
  error: Error = { error: { message: '', code: '' } };

  spinner = {
    color: 'primary',
    mode: 'indeterminate' as ProgressSpinnerMode,
    value: 50,
    diameter: 50,
  };

  /**
   * Constructor
   *
   * @param {FormBuilder} _formBuilder
   */
  constructor(
    private _formBuilder: FormBuilder,
    private _translateService: TranslateService,
    private _userService: UsersService,
    private _activatedRoute: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this._activatedRoute.params.subscribe(params => {
      this.recoveryToken = params['token'];
      this.userEmail = params['email'];
    });

    this.validateUserRequest();

    this.resetPasswordForm = this._formBuilder.group(
      {
        password: ['', Validators.required],
        passwordConfirmation: ['', [Validators.required]],
      },
      { validators: [CustomValidators.confirmPasswords()] }
    );
  }

  setDefaultSetting(): void {
    this.resetPasswordForm
      .get('password')
      .setValidators([
        Validators.required,
        CustomValidators.passwordNameValidator(
          this.currentUser.officialFirstName,
          this.currentUser.officialLastName
        ),
        CustomValidators.passwordSameAccountNameValidator(
          this.currentUser.samAccountName.toLowerCase()
        ),
        CustomValidators.passwordRestriction(),
        Validators.minLength(10),
        Validators.maxLength(50),
      ]);

    this.resetPasswordForm
      .get('passwordConfirmation')
      .setValidators([
        Validators.required,
        CustomValidators.passwordRestriction(),
        CustomValidators.passwordNameValidator(
          this.currentUser.officialFirstName,
          this.currentUser.officialLastName
        ),
        Validators.minLength(10),
        Validators.maxLength(50),
      ]);
  }

  getErrorMessage(): string {
    return this.resetPasswordForm.get('password').hasError('required')
      ? this._translateService.instant('ERROR.REQUIRED')
      : this.resetPasswordForm.get('password').hasError('passwordRestriction')
        ? this._translateService.instant('ERROR.COMPLEXITY')
        : this.resetPasswordForm.get('password').hasError('oldpasswordmatch')
          ? this._translateService.instant('ERROR.INTERDICTION')
          : this.resetPasswordForm
                .get('password')
                .hasError('passwordContainName')
            ? this._translateService.instant('ERROR.CONTAINNAME')
            : this.resetPasswordForm.get('password').hasError('minlength')
              ? this._translateService.instant('ERROR.MIN')
              : this.resetPasswordForm
                    .get('password')
                    .hasError('passwordContainSamAccountName')
                ? this._translateService.instant('ERROR.CONTAINSAMACCOUNTNAME')
                : this.resetPasswordForm.get('password').hasError('maxlength')
                  ? this._translateService.instant('ERROR.MAX')
                  : '';
  }

  async validateUserRequest(): Promise<void> {
    try {
      this.currentUser = await lastValueFrom(
        this._userService.requestPasswordValidation(
          this.recoveryToken,
          this.userEmail
        )
      );
      this.setDefaultSetting();
    } catch (error) {
      if (error.statusText == 'Unknown Error') {
        this.error = { error: { message: 'Unknown Error', code: 'ERR2' } };
      } else {
        if (error.error) {
          this.error = error.error;
          this.isTokenValid = false;
        } else {
          this.error = { error: { message: 'Unknown Error', code: 'ERR2' } };
        }
      }
    }
  }

  async resetPassword(): Promise<void> {
    this.isPasswordUpdate = false;
    this.errorUpdatePassword = false;

    const resetRequest: UserPasswordReset = {
      email: this.currentUser.email,
      password: this.resetPasswordForm.get('password').value,
    };

    try {
      this.isBusy = true;
      await lastValueFrom(
        this._userService.resetPassword(resetRequest, this.recoveryToken)
      ).then(() => {
        this.isPasswordUpdate = true;
      });

      // this.resetPasswordForm.reset();
      this.resetPasswordForm.controls['password'].disable();
      this.resetPasswordForm.controls['passwordConfirmation'].disable();
    } catch (error) {
      if (error.statusText == 'Unknown Error') {
        this.error = { error: { message: 'Unknown Error', code: 'ERR2' } };
      } else {
        if (error.error) {
          this.error = error.error;
        } else {
          this.error = { error: { message: 'Unknown Error', code: 'ERR2' } };
        }
      }

      this.isPasswordUpdate = false;
      this.errorUpdatePassword = true;
    } finally {
      this.isBusy = false;
    }
  }
}
